import { Component, input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ICallableObject } from '@df/api/request/request.service';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { IfSizeDirective } from '@df/ui/common/if-size.directive';
import { OrderByPipe } from '@df/ui/common/order-by.pipe';
import { AbstractFormComponent } from '@df/ui/form/abstract-form.component';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { InputWrapBodyDirective } from '../../../../../../ui/form/input-wrap-body.directive';
import { InputWrapComponent } from '../../../../../../ui/form/input-wrap.component';
import { NProductLayout } from '../../product-layout.interface';

export interface IProductDeliveryContent {
    returnNote?: string;
    returnLink?: string;
    returnLinkText?: string;
}

export interface IDeliveryPriceData {
    country_id?: string;
    product?: number;
    postcode?: string;
    region_id?: number;
}

export interface IDeliveryPriceRate extends Checkout.IShippingMethod {
    minPrice: number | null;
    sortOrder: number | null;
}

export interface IDeliveryPriceResult {
    rates: IDeliveryPriceRate[];
}

@Component({
    selector: 'product-layout-delivery-price',
    templateUrl: './product-layout-delivery-price.component.html',
    imports: [
        FormsModule,
        HrefComponent,
        ICmsClientModule,
        IfSizeDirective,
        InputWrapBodyDirective,
        InputWrapComponent,
        OrderByPipe,
        PriceComponent,
        TranslatePipe
    ]
})
export class ProductLayoutDeliveryPriceComponent extends AbstractFormComponent<IDeliveryPriceData> implements OnInit {
    readonly content = input.required<NProductLayout.IContent>();
    readonly defaultCountry = input<string>();
    readonly productId = input<number>();

    submitPath = 'delivery/product';

    rates?: IDeliveryPriceRate[];

    get countries() {
        return this.app.apiConfigService?.countries || [];
    }

    override ngOnInit(): void {
        super.ngOnInit();

        this.app.apiConfigService.loadCountries().then(() => this.detectChanges());

        this.data.country_id = this.app.customer.model?.data.geo || this.defaultCountry() || this.app.multisite.current.country;

        this.submit();
    }

    resetRates(result: IDeliveryPriceResult): void {
        this.rates = result && result.rates;
    }

    override dataReset(): void {
        // no data reset
    }

    protected override onSubmitSuccess(response: IDeliveryPriceResult): void {
        this.resetRates(response);

        super.onSubmitSuccess(response);

        this.detectChanges();
    }

    protected override getSubmitAction(): ICallableObject<Partial<IDeliveryPriceData>> {
        const productId = this.getProductId();

        this.data.product = productId;

        return { path: this.submitPath, data: this.data };
    }

    protected getProductId(): number {
        const productId = this.productId();

        if (productId) {
            const product = this.app.resources.product.getExisting(productId);

            if (product?.data && product?.isConfigurable) {
                const child = product.defaultChild;

                if (child) {
                    return child.id;
                }
            }

            return productId;
        }

        return 0;
    }
}
